import Cuboid from '../../rendering/Cuboid.js';

/**
 * Abstract base class for all editor tools
 * Provides common interface and utilities for tool implementations
 */
export default class BaseTool {
  /**
   * Create a new tool
   * @param {Object} editor - The VoxelEditor instance
   */
  constructor(editor) {
    this.editor = editor;
    this.world = editor.world;
    this.camera = editor.camera;
    this.collisionManager = editor.collisionManager;
    this.ghostPreview = editor.ghostPreview;

    // Tool state
    this.isActive = false;
    this.isDragging = false;
  }

  /**
   * Get the tool name (override in subclass)
   * @return {string} Tool name
   */
  get name() {
    return 'base';
  }

  /**
   * Called when tool is activated
   */
  activate() {
    this.isActive = true;
  }

  /**
   * Called when tool is deactivated
   */
  deactivate() {
    this.isActive = false;
    this.isDragging = false;
    this.ghostPreview.hide();
  }

  /**
   * Handle mouse down event
   * @param {MouseEvent} event - The mouse event
   */
  onMouseDown(event) {
    // Override in subclass
  }

  /**
   * Handle mouse move event
   * @param {MouseEvent} event - The mouse event
   */
  onMouseMove(event) {
    // Override in subclass
  }

  /**
   * Handle mouse up event
   * @param {MouseEvent} event - The mouse event
   */
  onMouseUp(event) {
    // Override in subclass
  }

  /**
   * Get screen coordinates relative to viewport center
   * @param {MouseEvent} event - The mouse event
   * @return {Object} Screen coordinates {x, y}
   */
  getScreenCoords(event) {
    const container = document.getElementById('container');
    const rect = container.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    };
  }

  /**
   * Get the face type from a DOM element
   * @param {HTMLElement} element - The face element
   * @return {string|null} Face type or null
   */
  getFaceType(element) {
    const faceTypes = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    for (const type of faceTypes) {
      if (element.classList.contains(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Get the cuboid from a DOM element
   * @param {HTMLElement} element - The face or cube element
   * @return {Cuboid|null} The cuboid instance or null
   */
  getCuboidFromElement(element) {
    const cubeEl = element.closest('.cube');
    if (!cubeEl) return null;
    return this.world.findCuboidByElement(cubeEl);
  }

  /**
   * Face normal offsets for adjacent placement
   */
  static FACE_OFFSETS = {
    top:    { x: 0, y: 1, z: 0 },
    bottom: { x: 0, y: -1, z: 0 },
    front:  { x: 0, y: 0, z: -1 },
    back:   { x: 0, y: 0, z: 1 },
    right:  { x: 1, y: 0, z: 0 },
    left:   { x: -1, y: 0, z: 0 }
  };

  /**
   * Update face selection to use a new cuboid key
   * Called after moving or extending a cuboid
   * @param {string} oldKey - The old cuboid key
   * @param {string} newKey - The new cuboid key
   */
  updateSelectionKey(oldKey, newKey) {
    if (!Cuboid.selectedFaces) return;

    // Update selectedFaces Set
    const updatedFaces = new Set();
    for (const serialized of Cuboid.selectedFaces) {
      if (serialized.startsWith(oldKey + '|')) {
        const face = serialized.substring(oldKey.length + 1);
        updatedFaces.add(`${newKey}|${face}`);
      } else {
        updatedFaces.add(serialized);
      }
    }
    Cuboid.selectedFaces = updatedFaces;

    // Update primarySelectedFace
    if (Cuboid.primarySelectedFace && Cuboid.primarySelectedFace.startsWith(oldKey + '|')) {
      const face = Cuboid.primarySelectedFace.substring(oldKey.length + 1);
      Cuboid.primarySelectedFace = `${newKey}|${face}`;
    }

    // Update selectedFace object
    if (Cuboid.selectedFace && Cuboid.selectedFace.cuboidKey === oldKey) {
      Cuboid.selectedFace.cuboidKey = newKey;
    }

    // Increment version to trigger re-render
    Cuboid.selectionVersion++;
  }
}
